import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './assignment.scss';
import {
  FormControl,
  FormError,
  FormLabel,
} from '@alefeducation/ui-elements-library-core';
import Calendar from '../common/Calendar/Calendar';
import Input from '../common/Input/Input';
import Select from '../common/Select/Select';
import { FormField, FormRow, Required } from './style';
import Buttons from './Buttons';
import AssignmentSection from './AssignmentSection';

export const AssignmentSettings = ({
  langCode,
  dateForCalendar,
  calRef,
  updateDateAction,
  assignmentDueDate,
  assignmentDueDateError,
  handleGradeableOptionChange,
  yesNoOptions,
  gradeableOption,
  handleAttachmentRequiredOptionChange,
  attachmentRequiredOption,
  isYes,
  handleCommentRequiredOptionChange,
  commentRequiredOption,
  setAssignmentMaxGrade,
  assignmentMaxGrade,
  assignmentMaxGradeError,
  assignmentMutation,
  buttonTooltip,
  goBackToPreviousPage,
  isFormValid,
  toggleModal,
  onUpdateCalendar,
}) => {
  const { t } = useTranslation();
  const [isCalendarOpen, setCalendarOpen] = useState(false);

  const handleClickOutside = event => {
    if (!calRef.current || !calRef.current.contains(event.target)) {
      document.removeEventListener('mousedown', handleClickOutside);
      setCalendarOpen(false);
    }
  };

  const openCalendar = () => {
    if (!isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    setCalendarOpen(true);
  };

  const closeCalendar = () => {
    document.removeEventListener('mousedown', handleClickOutside);
    setCalendarOpen(false);
  };

  const updateCalendar = value => {
    closeCalendar();
    onUpdateCalendar(value);
  };

  return (
    <AssignmentSection title={t('Assignment Settings')}>
      <FormRow>
        <FormControl appearance={assignmentDueDateError ? 'error' : 'regular'}>
          <FormLabel data-testid={'assignment-dueDate-input-label'}>
            <Required>*</Required>
            &nbsp;
            {t('Due Date')}
          </FormLabel>
          {isCalendarOpen && (
            <Calendar
              value={dateForCalendar}
              onClickDay={updateCalendar}
              ref={calRef}
            />
          )}
          <Input
            onChange={e => updateDateAction(e.target.value.trim())}
            onClick={openCalendar}
            value={assignmentDueDate}
            testId="assignment-dueDate"
          />
          <FormError>{assignmentDueDateError}</FormError>
        </FormControl>
        <FormField />
      </FormRow>
      <FormRow>
        <FormControl>
          <FormLabel data-testId="gradeable-assignment-select-label">
            {t('Graded Assignment?')}
          </FormLabel>
          <Select
            testId="gradeable-assignment-select"
            selectionChanged={handleGradeableOptionChange}
            textKey={'key'}
            menuItems={yesNoOptions}
            selectedItem={gradeableOption}
            lang={langCode}
            fullWidth
          />
        </FormControl>
        <FormControl appearance={assignmentMaxGradeError ? 'error' : 'regular'}>
          <FormLabel data-testid={'assignment-maxGrade-input-label'}>
            {t('Max Grade')}
          </FormLabel>
          <Input
            onChange={e => setAssignmentMaxGrade(e.target.value.trim())}
            value={assignmentMaxGrade}
            testId="assignment-maxGrade"
            disabled={!isYes(gradeableOption)}
          />
          {!assignmentMaxGradeError && (
            <div className="helper-text">{t('Max 100')}</div>
          )}
          <FormError>{assignmentMaxGradeError}</FormError>
        </FormControl>
      </FormRow>
      <FormRow>
        <FormControl>
          <FormLabel data-testId="attachment-required-select-label">
            {t('Requires File Submission?')}
          </FormLabel>
          <Select
            testId="attachment-required-select"
            selectionChanged={handleAttachmentRequiredOptionChange}
            textKey={'key'}
            menuItems={yesNoOptions}
            selectedItem={attachmentRequiredOption}
            lang={langCode}
            fullWidth
          />
        </FormControl>
        <FormControl disabled={isYes(attachmentRequiredOption)}>
          <FormLabel data-testId="comment-required-select-label">
            {t('Requires Comment Submission?')}
          </FormLabel>
          <Select
            testId="comment-required-select"
            selectionChanged={handleCommentRequiredOptionChange}
            textKey={'key'}
            menuItems={yesNoOptions}
            selectedItem={commentRequiredOption}
            lang={langCode}
            fullWidth
            disabled={isYes(attachmentRequiredOption)}
          />
        </FormControl>
      </FormRow>
      <Buttons
        assignmentMutation={assignmentMutation}
        buttonTooltip={buttonTooltip}
        goBackToPreviousPage={goBackToPreviousPage}
        isFormValid={isFormValid}
        toggleModal={toggleModal}
      />
    </AssignmentSection>
  );
};

AssignmentSettings.propTypes = {
  langCode: PropTypes.string.isRequired,
  assignmentMaxGrade: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  assignmentDueDate: PropTypes.string.isRequired,
  dateForCalendar: PropTypes.any.isRequired,
  assignmentDueDateError: PropTypes.string.isRequired,
  assignmentMaxGradeError: PropTypes.string.isRequired,
  gradeableOption: PropTypes.object.isRequired,
  yesNoOptions: PropTypes.array.isRequired,
  attachmentRequiredOption: PropTypes.object.isRequired,
  commentRequiredOption: PropTypes.object.isRequired,
  calRef: PropTypes.object.isRequired,
  buttonTooltip: PropTypes.string.isRequired,
  isYes: PropTypes.func.isRequired,
  setAssignmentMaxGrade: PropTypes.func.isRequired,
  toggleModal: PropTypes.func.isRequired,
  handleGradeableOptionChange: PropTypes.func.isRequired,
  updateDateAction: PropTypes.func.isRequired,
  handleAttachmentRequiredOptionChange: PropTypes.func.isRequired,
  handleCommentRequiredOptionChange: PropTypes.func.isRequired,
  assignmentMutation: PropTypes.func.isRequired,
  goBackToPreviousPage: PropTypes.func.isRequired,
  isFormValid: PropTypes.bool.isRequired,
  onUpdateCalendar: PropTypes.func.isRequired,
};
